import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Boxes } from 'lucide-react';
import { useWhoami } from '@cotera/client/app/stores/org';
import { CocoEvent, useTracking } from '@cotera/client/app/etc';
import {
  ResourceListView,
  ResourceListSecondaryCells,
  type Column,
  type FilterOption,
  type ItemAction,
} from '../resources/list/resource-list-view';
import {
  useAction,
  useInScopeModel,
} from '@cotera/client/v0/actions/framework';
import { CancelWorkflowRunsAction } from './cancel-workflow-runs.action';
import { RetryWorkflowRunsAction } from './retry-workflow-runs.action';
import {
  WorkflowRunActionTargetsModel,
  canCancelWorkflowRun,
  canRetryWorkflowRun,
} from './workflow-run-action-targets.model';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import {
  useWorkflowRunsModel,
  type WorkflowRunsFilters,
} from './workflow-runs.model';
import type { WorkflowRun } from './workflow-run.types';
import {
  WORKFLOW_BUCKET_FLUSH_TRIGGER_TYPE,
  WorkflowRunAttemptBadge,
  WorkflowRunIdempotencyKeyBadge,
  WorkflowRunStatusBadge,
  WorkflowRunTestBadge,
  WorkflowRunTriggerCell,
  formatDuration,
  triggerLabel,
} from './workflow-run-status';
import {
  DEFAULT_DATE_RANGE,
  DateRangeFilter,
  dateRangeBounds,
  isDefaultDateRange,
  type DateRangeValue,
} from './workflow-runs-filters';
import { WorkflowRunsStatsPanel } from './workflow-runs-stats';

const RUN_COLUMNS: Column[] = [
  { label: 'Run' },
  { label: 'Trigger', width: '280px' },
  { label: 'Status', width: '130px' },
  { label: 'Duration', width: '110px' },
  { label: 'Items', width: '90px' },
];

/** Selectable run statuses for the status filter, in display order. */
const STATUS_FILTERS: { key: string; label: string }[] = [
  { key: 'queued', label: 'Queued' },
  { key: 'incomplete', label: 'In progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'failed', label: 'Failed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'stopped', label: 'Stopped' },
];

/** Short relative timestamp ("3m ago", "2h ago", or a date for older runs). */
const formatRelative = (date: Date): string => {
  const diffMs = Date.now() - date.getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) {
    return 'just now';
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days}d ago`;
  }
  return date.toLocaleDateString();
};

/**
 * Exact run time for row headings: the clock time ("2:34:12 PM") leads, the
 * date ("Oct 16") trails muted. Seconds are kept because high-volume triggers
 * (webhooks) land many runs inside one minute, and a relative "3d ago" can't
 * tell them apart. The year only shows when it isn't the current one.
 */
const RunTimeHeading: React.FC<{ date: Date }> = ({ date }) => (
  <p
    className="text-sm whitespace-nowrap shrink-0"
    title={formatRelative(date)}
  >
    <span className="font-medium text-slate-800">
      {date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      })}
    </span>
    <span className="ml-1.5 text-muted-foreground">
      {date.toLocaleDateString(undefined, {
        ...(date.getFullYear() !== new Date().getFullYear() && {
          year: 'numeric',
        }),
        month: 'short',
        day: 'numeric',
      })}
    </span>
  </p>
);

export const WorkflowRunsList: React.FC<{
  workflowId: string;
  layout?: 'page' | 'embedded' | 'panel';
  /** When set, clicking a run invokes this instead of navigating to the run detail route. */
  onClickRun?: (run: WorkflowRun) => void;
  /**
   * Opens a run by id — used after a single-run retry, where the new run is not
   * in the loaded list yet so there is no row to hand to `onClickRun`.
   */
  onOpenRunId?: (runId: string) => void;
}> = ({ workflowId, layout = 'page', onClickRun, onOpenRunId }) => {
  const org = useWhoami((s) => s.org);
  const navigate = useNavigate();
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [bucketFlushesOnly, setBucketFlushesOnly] = useState(false);
  const [dateRange, setDateRange] =
    useState<DateRangeValue>(DEFAULT_DATE_RANGE);

  const model = useWorkflowRunsModel();
  const runsData = useWatchableValue(model.runsDataWatchable);
  const hasMore = useWatchableValue(model.hasMoreWatchable);
  const triggersById = useWatchableValue(model.triggersByIdWatchable);
  const hasBucketNode = useWatchableValue(model.hasBucketNodeWatchable);

  // Only honored while the workflow has a bucket: if the bucket is removed the
  // option disappears from the menu, and a hidden filter must not keep
  // narrowing the list.
  const filterBucketFlushes = hasBucketNode && bucketFlushesOnly;

  // One consolidated filters object; date bounds are computed once per
  // selection (stable across pagination).
  const filters = useMemo<WorkflowRunsFilters>(
    () => ({
      statuses: selectedStatuses,
      ...(filterBucketFlushes && {
        triggerTypes: [WORKFLOW_BUCKET_FLUSH_TRIGGER_TYPE],
      }),
      ...dateRangeBounds(dateRange),
    }),
    [selectedStatuses, filterBucketFlushes, dateRange]
  );
  const runs = runsData?.runs ?? [];
  const total = runsData?.total ?? 0;
  const status: 'loading' | 'ready' = runsData === null ? 'loading' : 'ready';

  // The model owns the runs fetch; push local filter UI state into it.
  useEffect(() => {
    model.setFilters(filters);
  }, [model, filters]);

  useTracking(CocoEvent.WorkflowRunsViewed, { workflowId });

  // The list hides its filter menu once it is empty unless told a filter is
  // what emptied it — without this, picking a filter that matches no runs
  // strands you with no menu to undo it.
  const filtersActive =
    selectedStatuses.length > 0 ||
    filterBucketFlushes ||
    !isDefaultDateRange(dateRange);

  const clearFilters = useCallback(() => {
    setSelectedStatuses([]);
    setBucketFlushesOnly(false);
    setDateRange(DEFAULT_DATE_RANGE);
  }, []);

  const toggleStatus = useCallback((key: string, checked: boolean) => {
    setSelectedStatuses((prev) =>
      checked ? [...prev, key] : prev.filter((s) => s !== key)
    );
  }, []);

  const filterOptions: FilterOption[] = useMemo(() => {
    const statusOptions: FilterOption[] = STATUS_FILTERS.map((s) => ({
      label: s.label,
      key: s.key,
      checked: selectedStatuses.includes(s.key),
      onChange: (checked: boolean) => toggleStatus(s.key, checked),
      ...(hasBucketNode && { section: 'Status' }),
    }));
    if (!hasBucketNode) {
      return statusOptions;
    }
    // A bucket rides on a high-volume workflow, where its one flush run is
    // buried among hundreds of feeder runs; this pulls just the flushes.
    return [
      {
        label: 'Bucket flushes only',
        key: WORKFLOW_BUCKET_FLUSH_TRIGGER_TYPE,
        checked: bucketFlushesOnly,
        onChange: setBucketFlushesOnly,
        icon: <Boxes />,
        section: 'Run type',
      },
      ...statusOptions,
    ];
  }, [selectedStatuses, toggleStatus, hasBucketNode, bucketFlushesOnly]);

  const runPath = useCallback(
    (runId: string) => `/${org.slug}/workflows/${workflowId}/runs/${runId}`,
    [org.slug, workflowId]
  );

  // Even where a click opens the run in a panel, the row links to the run's
  // own page, so ⌘-click / middle-click open it in a new tab.
  const runHref = useCallback((run: WorkflowRun) => runPath(run.id), [runPath]);

  const handleClickRun = useCallback(
    (run: WorkflowRun) => {
      if (onClickRun !== undefined) {
        onClickRun(run);
        return;
      }
      navigate(runPath(run.id));
    },
    [navigate, onClickRun, runPath]
  );

  const targetsModel = useInScopeModel(WorkflowRunActionTargetsModel);
  const cancelRunsAction = useAction(CancelWorkflowRunsAction);
  const retryRunsAction = useAction(RetryWorkflowRunsAction);

  const openRunId = useCallback(
    (runId: string) => {
      if (onOpenRunId !== undefined) {
        onOpenRunId(runId);
        return;
      }
      navigate(runPath(runId));
    },
    [navigate, onOpenRunId, runPath]
  );

  // Keep the action targets in step with what the list has selected, so the
  // bulk bar acts on the selection...
  const handleFocusRuns = useCallback(
    (runs: WorkflowRun[]) => {
      targetsModel.setRuns(runs);
    },
    [targetsModel]
  );

  // ...and retarget to a single row when its action menu opens.
  const handleOpenActionMenu = useCallback(
    (run: WorkflowRun) => {
      targetsModel.setRuns([run]);
    },
    [targetsModel]
  );

  // Both menus build from the same helper, so their status gating can never
  // drift apart. A mixed selection shows both entries, each labelled with the
  // subset it applies to, so it is never silently acting on fewer rows than are
  // selected.
  const runActionsFor = useCallback(
    (runs: WorkflowRun[]): ItemAction[] => {
      const cancellable = runs.filter(canCancelWorkflowRun);
      const retryable = runs.filter(canRetryWorkflowRun);
      const actions: ItemAction[] = [];
      if (retryable.length > 0) {
        actions.push({
          label:
            retryable.length === runs.length
              ? runs.length === 1
                ? 'Retry run'
                : 'Retry'
              : `Retry ${retryable.length}`,
          icon: 'arrow-path',
          onClick: () => {
            targetsModel.setRuns(retryable);
            void retryRunsAction.execute({}).then((result) => {
              // A single-run retry follows the new run; a bulk retry has no one
              // run to open, so it just refreshes the list.
              const createdRunId = result.isOk()
                ? result.value.resourceId
                : undefined;
              if (createdRunId !== undefined) {
                openRunId(createdRunId);
              }
            });
          },
        });
      }
      if (cancellable.length > 0) {
        actions.push({
          label:
            cancellable.length === runs.length
              ? runs.length === 1
                ? 'Cancel run'
                : 'Cancel'
              : `Cancel ${cancellable.length}`,
          icon: 'x-circle',
          variant: 'destructive',
          onClick: () => {
            targetsModel.setRuns(cancellable);
            void cancelRunsAction.execute({});
          },
        });
      }
      return actions;
    },
    [targetsModel, cancelRunsAction, retryRunsAction, openRunId]
  );

  const runItemActions = useCallback(
    (run: WorkflowRun): ItemAction[] => runActionsFor([run]),
    [runActionsFor]
  );

  const filterRun = useCallback(
    (run: WorkflowRun, query: string): boolean => {
      const trigger = triggersById.get(run.triggerId);
      return (
        run.status.toLowerCase().includes(query) ||
        triggerLabel(run.triggerType, trigger).toLowerCase().includes(query) ||
        (run.error ?? '').toLowerCase().includes(query) ||
        (run.idempotencyKey ?? '').toLowerCase().includes(query)
      );
    },
    [triggersById]
  );

  const renderRun = useCallback(
    (run: WorkflowRun) => {
      const relativeTitle = formatRelative(run.createdAt);
      const trigger = triggersById.get(run.triggerId);
      const duration = formatDuration(run.startedAt, run.finishedAt);

      if (layout === 'panel') {
        return (
          <div className="min-w-0 w-full">
            <div className="flex items-center justify-between gap-2 min-w-0">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <RunTimeHeading date={run.createdAt} />
                {run.isTest ? <WorkflowRunTestBadge /> : null}
                <WorkflowRunAttemptBadge retryNum={run.retryNum} />
                <WorkflowRunIdempotencyKeyBadge
                  idempotencyKey={run.idempotencyKey}
                />
              </div>
              <div className="shrink-0">
                <WorkflowRunStatusBadge status={run.status} />
              </div>
            </div>
            {run.error !== null && run.error !== '' ? (
              <p className="text-[11px] text-destructive truncate mt-0.5">
                {run.error}
              </p>
            ) : (
              <div className="flex items-center gap-x-3 mt-0.5 min-w-0 text-[11px] text-muted-foreground">
                <WorkflowRunTriggerCell
                  triggerType={run.triggerType}
                  trigger={trigger}
                />
                <span className="tabular-nums shrink-0">{duration}</span>
                {run.itemsProcessed !== null ? (
                  <span
                    className="tabular-nums shrink-0"
                    title="Items processed"
                  >
                    {run.itemsProcessed}{' '}
                    {run.itemsProcessed === 1 ? 'item' : 'items'}
                  </span>
                ) : null}
              </div>
            )}
          </div>
        );
      }

      if (layout === 'embedded') {
        return (
          <div className="min-w-0 w-full">
            <div className="flex items-start justify-between gap-2 min-w-0">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <RunTimeHeading date={run.createdAt} />
                {run.isTest ? <WorkflowRunTestBadge /> : null}
                <WorkflowRunAttemptBadge retryNum={run.retryNum} />
                <WorkflowRunIdempotencyKeyBadge
                  idempotencyKey={run.idempotencyKey}
                />
              </div>
              <div className="shrink-0">
                <WorkflowRunStatusBadge status={run.status} />
              </div>
            </div>
            {run.error !== null && run.error !== '' ? (
              <p className="text-[11px] text-destructive truncate mt-0.5">
                {run.error}
              </p>
            ) : null}
            <div className="flex items-center gap-x-3 gap-y-1 mt-1 min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0 flex-1 text-sm">
                <WorkflowRunTriggerCell
                  triggerType={run.triggerType}
                  trigger={trigger}
                />
                <span className="text-muted-foreground tabular-nums">
                  {duration}
                </span>
                {run.itemsProcessed !== null ? (
                  <span
                    className="text-muted-foreground tabular-nums"
                    title="Items processed"
                  >
                    {run.itemsProcessed}{' '}
                    {run.itemsProcessed === 1 ? 'item' : 'items'}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        );
      }

      return (
        <>
          <div className="min-w-0 w-full @list-cols/list:flex-1 @list-cols/list:min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <RunTimeHeading date={run.createdAt} />
              {run.isTest ? <WorkflowRunTestBadge /> : null}
              <WorkflowRunAttemptBadge retryNum={run.retryNum} />
              <WorkflowRunIdempotencyKeyBadge
                idempotencyKey={run.idempotencyKey}
              />
            </div>
            <p className="text-[11px] text-muted-foreground/60 truncate mt-0.5">
              {run.error !== null && run.error !== ''
                ? run.error
                : relativeTitle}
            </p>
          </div>
          <ResourceListSecondaryCells>
            <div className="shrink-0 text-left @list-cols/list:w-[280px] @list-cols/list:pl-3 min-w-0 text-sm">
              <WorkflowRunTriggerCell
                triggerType={run.triggerType}
                trigger={trigger}
              />
            </div>
            <div className="shrink-0 text-left @list-cols/list:w-[130px] @list-cols/list:pl-3">
              <WorkflowRunStatusBadge status={run.status} />
            </div>
            <div className="shrink-0 text-left @list-cols/list:w-[110px] @list-cols/list:pl-3 text-sm text-muted-foreground">
              {duration}
            </div>
            <div
              className="shrink-0 text-left @list-cols/list:w-[90px] @list-cols/list:pl-3 text-sm text-muted-foreground tabular-nums"
              title="Most items processed in one step (e.g. a for-each fan-out)"
            >
              {run.itemsProcessed ?? '—'}
            </div>
          </ResourceListSecondaryCells>
        </>
      );
    },
    [layout, triggersById]
  );

  return (
    <ResourceListView
      layout={layout}
      headerSize="compact"
      title="Runs"
      noun="run"
      items={runs}
      totalCount={total}
      status={status}
      filterFn={filterRun}
      renderItem={renderRun}
      icon={Activity}
      keyFn={(run) => run.id}
      onClickItem={handleClickRun}
      itemHref={runHref}
      itemActions={runItemActions}
      selectionActions={runActionsFor}
      onFocus={handleFocusRuns}
      onOpenActionMenu={handleOpenActionMenu}
      emptyIcon={
        <Activity className="h-12 w-12 text-muted-foreground/50 mb-4" />
      }
      emptyText="No runs yet"
      noMatchText="No runs match your filters"
      searchPlaceholder="Search runs..."
      filterOptions={filterOptions}
      filtersActive={filtersActive}
      onClearFilters={clearFilters}
      toolbarExtras={
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
      }
      headerExtras={layout === 'panel' ? undefined : <WorkflowRunsStatsPanel />}
      columns={RUN_COLUMNS}
      hasMore={hasMore}
      onLoadMore={() => model.loadMore()}
      isLoadingMore={false}
    />
  );
};
